import { agencyPath, agentPath, createRequestWithComputedFilters, customerPath } from '@/utils/api'
import store from '@/store'
import network from '@/utils/network'
import { getStripe } from '@/utils/webservices/stripe'
import { LISTE_TYPE_EVENTS } from '@/consts/events'

/**
 * Récupère la liste des agents
 * @returns {Promise<void>}
 */
export const fetchAgencyAgents = async (params = {}, availables = false) => {
  const slugAgency = store.getters['user/agencySlug']
  const r = await network.post(`agency/${slugAgency}/fetch/agents${availables ? '/availables' : ''}`, { orderBy: 'nomPrenom', ...params })
  return r.data
}

/**
 * Récupère les infos d'horaire d'un agent
 * @returns {Promise<void>}
 */
export const fetchAgencyAgentHours = createRequestWithComputedFilters(async (params) => {
  const slugAgency = store.getters['user/agencySlug']
  const slugAgent = store.getters['user/agentSlug']
  return network.post(`agency/${slugAgency}/agent/${slugAgent}/fetch/hours`, params)
})

/**
 * Récupère la liste des administrateurs
 * @returns {Promise}
 */
export const fetchAgencyManager = async () => {
  const slugAgency = store.getters['user/agencySlug']
  const { data: { managers } } = await network.post(`agency/${slugAgency}/fetch/managers`)
  return managers
}

/**
 * Récupère la liste des clients
 * @returns {Promise<>}
 */
export const fetchAgencyClients = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/fetch/customers`, params)
    return r.data.data
  }
)

/**
 * Récupère la formule de paiement de l'agence
 * @returns {Promise<void>}
 */
export const getAgencyPricing = async () => {
  const slugAgency = store.getters['user/agencySlug']
  const r = await network.get(`agency/${slugAgency}/pricing`)
  return r.data.pricing
}

/**
 * Récupère la liste des sites
 * @returns {Promise<>}
 */
export const fetchAgencySites = async () => {
  let url = agencyPath()
  const isAgent = store.getters['user/isAgent']
  if (isAgent) {
    const slugAgent = store.getters['user/agentSlug']
    url += `/agent/${slugAgent}`
  }
  const r = await network.post(`${url}${customerPath()}/fetch/sites`)
  return r.data
}

/**
 * Récupère la liste des événements
 * @returns {Promise<Array>}
 */
export const fetchAllEvents = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug'] + customerPath()
    const r = await network.post(`agency/${slugAgency}/fetch/allEvents`, params)
    return r.data
  },
  {
    limit: 10,
    orderBy: 'createdAt',
    ascending: 0,
    filters: {
      sites: [],
      customers: [],
      start: '',
      end: ''
    }
  }
)

/**
 * Récupère le chiffre d'affaire de l'agence
 * @param filters
 * @param opt
 */
export const fetchTurnover = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/fetch/turnover`, params)
    return r.data
  },
  {
    filters: {
      start: '',
      end: '',
      shortcut: 'year'
    }
  })

/**
 * Détail des heures
 * @param filters
 * @param opt
 * @returns {Promise}
 */
export const fetchHourDetail = createRequestWithComputedFilters(
  async (params) => {
    const r = await network.post(`${agencyPath()}${customerPath()}${agentPath()}/fetch/hour-detail`, params)
    return r.data
  })

/**
 * Récupère les indisponibilités
 * @param filters
 * @param opt
 * @returns {Promise}
 */
export const fetchAgencyUnavailabilities = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/fetch/unavailabilities`, params)
    return r.data
  })

/**
 * Récupère les expirations des agents
 * @param filters
 * @param opt
 * @returns {Promise}
 */
export const fetchAgencyAgentsExpirations = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/agents/fetch/expirations`, params)
    return r.data
  })

/**
 * Récupère les expirations des contrats clients
 * @param filters
 * @param opt
 * @returns {Promise}
 */
export const fetchAgencyCustomersContractsExpirations = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/customers/fetch/contracts`, params)
    return r.data
  })

/**
 * Récupère les contrats
 * @param filters
 * @param opt
 * @returns {Promise}
 */
export const fetchAgencyContracts = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/fetch/contracts`, params)
    return r.data
  })

/**
 * Récupère les items des agents
 * @param filters
 * @param opt
 * @returns {Promise}
 */
export const fetchAgencyItems = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/agent/fetch/items`, params)
    return r.data
  })

/**
 * Créé une agence
 * @returns {Promise<>}
 */
export const postAgency = async (agency) => {
  const serviceEventTypes = LISTE_TYPE_EVENTS.filter((evt) => evt.defaultValue && evt.id).map((event) => ({
    ...event.defaultValue,
    serviceEventType: { id: event.id }
  }))
  const r = await network.post(`agency`, {
    ...agency,
    serviceEventTypes
  })
  return r.data
}

/**
 * Met à jour une agence
 * @returns {Promise<>}
 */
export const putAgency = async (agency) => {
  const r = await network.put(`agency`, agency)
  return r.data
}

/**
 * Récupère les cards
 * @returns {Promise<>}
 */
export const fetchAgencyCards = async () => {
  const slugAgency = store.getters['user/agencySlug']
  const r = await network.get(`agency/${slugAgency}/cards`)
  return r.data
}

/**
 * Génère une checkout session Stripe
 * @returns {Promise<>}
 */
export const getAgencyCardDetails = async (action = 'home') => {
  const { data } = await network.get(`${agencyPath()}/card-details`, {
    params: {
      action
    }
  })
  const stripe = await getStripe()
  await stripe.redirectToCheckout(data)
  return data
}

/**
 * Récupère les informations pour finaliser un paiement
 * @returns {Promise<>}
 */
export const getAgencyPaymentIntent = async ({ reference }) => {
  const { data } = await network.get(`${agencyPath()}/payment-intent/${reference}`)
  return data
}

/**
 * Demande une tentative pour fixer les défauts de paiement
 * @returns {Promise<>}
 */
export const getAgencyPaymentsFix = async () => {
  const { data } = await network.get(`${agencyPath()}/payments-fix`)
  return data
}

/**
 * Récupère les comtpes
 * @returns {Promise<>}
 */
export const fetchAgencyBankAccounts = async () => {
  const slugAgency = store.getters['user/agencySlug']
  const r = await network.get(`agency/${slugAgency}/bank-accounts`)
  return r.data
}

/**
 * Génère une URL GoCardLess
 * @returns {Promise<>}
 */
export const postAgencyBankDetails = async (action = 'home') => {
  const slugAgency = store.getters['user/agencySlug']
  const r = await network.post(`agency/${slugAgency}/bank-details`, {}, {
    params: {
      action
    }
  })
  return r.data
}

/**
 * Récupère les paiements
 * @returns {Promise<>}
 */
export const fetchAgencyPayments = async () => {
  const slugAgency = store.getters['user/agencySlug']
  const r = await network.post(`agency/${slugAgency}/fetch/payments`)
  return r.data
}

/**
 * Récupère tous les paiements
 * @returns {Promise<>}
 */
export const fetchAgenciesPayments = createRequestWithComputedFilters(
  async (filters) => {
    const r = await network.post(`admin/fetch/payments`, filters)
    return r.data
  })

/**
 * Récupère les sousciptions de l'agence
 * @returns {Promise<>}
 */
export const fetchAgencySubscriptions = async () => {
  const slugAgency = store.getters['user/agencySlug']
  const r = await network.get(`agency/${slugAgency}/subscriptions`)
  return r.data
}

/**
 * Met à jour la formule de l'agence
 * @returns {Promise<>}
 * @param options
 */
export const patchAgencyPlan = async (options = {}) => {
  const slugAgency = store.getters['user/agencySlug']
  const r = await network.patch(`agency/${slugAgency}/plan`, options)
  return r.data
}

/**
 * Récupère les erreur de disponibilité du planning
 * @param filters
 * @param opt
 * @returns {Promise}
 */
export const fetchPlanningErrorAvailable = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/fetch/planning-error/available`, params)
    return r.data
  })

/**
 * Récupère les erreur de temps de repos du planning
 * @param filters
 * @param opt
 * @returns {Promise}
 */
export const fetchPlanningErrorRest = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/fetch/planning-error/rest`, params)
    return r.data
  })

/**
 * Récupère les erreur de temps de repos du planning
 * @param filters
 * @param opt
 * @returns {Promise}
 */
export const fetchPlanningErrorWorkingTime = createRequestWithComputedFilters(
  async (params) => {
    const slugAgency = store.getters['user/agencySlug']
    const r = await network.post(`agency/${slugAgency}/fetch/planning-error/working-time`, params)
    return r.data
  })

/**
 * Récupère les constantes de l'application client/server
 * @returns {Promise<>}
 */
export const getAgencyConfig = async () => {
  const slugAgency = store.getters['user/agencySlug']
  const r = await network.get(`${slugAgency ? `agency/${slugAgency}` : ''}${customerPath()}/config`)
  return r.data
}
